import React from 'react';

import { FormField, FormHelp, Input, Label, Spacer, Text, Textarea } from '@zillow/constellation';

import type { HTMLElementEvent } from '../../../../models/Types';
import OptionSelector from './OptionSelector';
import HeaderToggle from './HeaderToggle';
import type { Section } from '../../../../models';
import { ELECTRICITY } from '../../../../constants';
import { isMultiFamily } from './MultiFamilyLandlordUtility';
import { CenteredHR } from '../../../Widgets/CenteredHR';
import YesAndNo from '../../../Widgets/YesAndNo';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function Electricity_v3({ section, setSectionValues }: Props): JSX.Element {
  function setLandlordResponsibility(): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.TENANT_RESPONSIBILITY]: false,
      [ELECTRICITY.FIELD_NAME.LANDLORD_RESPONSIBILITY]: true,
      [ELECTRICITY.FIELD_NAME.MULTIFAMILY_TENANT_RESPONSIBILITY.DELIVERED_TO_AREAS_OTHER_THAN_TENANTS_UNIT]: null,
    });
  }

  function setTenantResponsibility(): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.TENANT_RESPONSIBILITY]: true,
      [ELECTRICITY.FIELD_NAME.LANDLORD_RESPONSIBILITY]: false,
      [ELECTRICITY.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.BILL_AMOUNT]: '',
    });
  }

  function setServiceProvider(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.SERVICE_PROVIDER]: event.currentTarget.value,
    });
  }

  function setBillAmount(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.BILL_AMOUNT]: event.currentTarget.value,
    });
  }

  function toggleUtility(): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(ELECTRICITY.FIELD_NAME.IS_ENABLED),
    });
  }

  function yesDeliveredToAreas(): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.MULTIFAMILY_TENANT_RESPONSIBILITY.DELIVERED_TO_AREAS_OTHER_THAN_TENANTS_UNIT]: true,
    });
  }

  function noDeliveredToAreas(): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.MULTIFAMILY_TENANT_RESPONSIBILITY.DELIVERED_TO_AREAS_OTHER_THAN_TENANTS_UNIT]: false,
    });
  }

  const isMultiFamilyLandlord = isMultiFamily(section);
  const electricity_isEnabled = section.getFieldValue(ELECTRICITY.FIELD_NAME.IS_ENABLED);
  const tenantSelected = section.getFieldValue(ELECTRICITY.FIELD_NAME.TENANT_RESPONSIBILITY);
  const landlordSelected = section.getFieldValue(ELECTRICITY.FIELD_NAME.LANDLORD_RESPONSIBILITY);
  const serviceProvider = section.getFieldValue(ELECTRICITY.FIELD_NAME.SERVICE_PROVIDER);

  return (
    <>
      <HeaderToggle
        header="Electricity"
        subheader="Tenant pays in addition to rent"
        switchOn={electricity_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {electricity_isEnabled && (
        <>
          <CenteredHR />
          <Spacer marginBottom="sm">
            <OptionSelector
              disabled={!electricity_isEnabled}
              optionsText="Who will be responsible for electricity set up?"
              option1Content="Responsible for account set up and direct payments to specified service provider."
              option2Content="Responsible for account set up and will bill tenant separately."
              option1Selected={tenantSelected}
              onOption1Click={setTenantResponsibility}
              option2Selected={landlordSelected}
              onOption2Click={setLandlordResponsibility}
            />
          </Spacer>

          {tenantSelected && (
            <>
              <FormField marginBottom={isMultiFamilyLandlord ? 'sm' : ''}>
                <Label id="tenantSelected">What service provider?</Label>
                <Input
                  data-test="tenantSelectedInput"
                  aria-labelledby="tenantSelected"
                  placeholder="(example: Sunrise Electric Company)"
                  value={serviceProvider}
                  onChange={setServiceProvider}
                />
              </FormField>
            </>
          )}

          {isMultiFamilyLandlord && tenantSelected && (
            <>
              <Text>Will this electricity service be delivered to areas other than the tenant’s unit?</Text>
              <YesAndNo
                yesActive={section.getFieldValue(
                  ELECTRICITY.FIELD_NAME.MULTIFAMILY_TENANT_RESPONSIBILITY.DELIVERED_TO_AREAS_OTHER_THAN_TENANTS_UNIT,
                )}
                yesHandler={yesDeliveredToAreas}
                noHandler={noDeliveredToAreas}
              />
            </>
          )}

          {isMultiFamilyLandlord && landlordSelected && (
            <FormField>
              <Label id="landlordSelected">How will you determine the amount to bill the tenant for electricity?</Label>
              <Textarea
                aria-labelledby="landlordSelected"
                onChange={setBillAmount}
                value={section.getFieldValue(ELECTRICITY.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.BILL_AMOUNT)}
                resize="vertical"
              />
              <FormHelp>
                Example: The provider bills me per kWh used by the entire building. I distribute that cost evenly among
                each of the building’s 5 units. Each unit pays 1/5 of the total cost.
              </FormHelp>
            </FormField>
          )}
        </>
      )}
    </>
  );
}
